<template>
  <Loading
    :loading="saveLoading"
    loading-text="Loading scheduling window..."
  >
    <div class="section-block">
      <div class="flex">
        <div class="min-w-[528px]">
          <div class="flex items-center">
            <img src="@/assets/my-calendar/availability/edit-calendar.svg" alt="calendar" />
            <div class="card-title ml-2">Appointment Scheduling Window</div>
          </div>
          <div class="mt-1 text-[12px] text-[#999999]">
            Define how soon and how far out appointments can be booked on your calendar.
          </div>
        </div>
        <ResponsiveButton
          data-testid="my-calendar.appointment-scheduling-window-section.save-btn"
          button_type="filled"
          built_in_theme="dark"
          size="medium"
          display_name="Save"
          :disabled="isSaveDisabled"
          @click-button="handleSave"
        />
      </div>

      <div class="field-row mt-6">
        <div class="field-label">Soonest available appointment</div>
        <div class="flex items-center gap-[7px]">
          <Input
            v-model="localSoonestValue"
            data-testid="my-calendar.appointment-scheduling-window-section.soonest-value-input"
            :validate-func="validateNonNegativeInteger"
            placeholder=""
            class="scheduling-window-input"
          />

          <div class="window-select-wrap ">
            <SingleSelector
              v-model="localSoonestUnit"
              data-testid="my-calendar.appointment-scheduling-window-section.soonest-unit-select"
              class="window-select"
              :options="soonestUnitOptions"
              size="medium"
              placeholder="Select"
            />
          </div>
        </div>
      </div>

      <div class="field-row mt-6">
        <div class="field-label">Furthest available appointment</div>
        <div class="flex items-center gap-[7px]">
          <Input
            v-model="localFurthestValue"
            data-testid="my-calendar.appointment-scheduling-window-section.furthest-value-input"
            :validate-func="validateNonNegativeInteger"
            placeholder=""
            class="scheduling-window-input"
          />
          <div class="window-select-wrap ">
            <SingleSelector
              v-model="localFurthestUnit"
              data-testid="my-calendar.appointment-scheduling-window-section.furthest-unit-select"
              class="window-select"
              :options="furthestUnitOptions"
              size="medium"
              placeholder="Select"
            />
          </div>
        </div>
      </div>
    </div>
  </Loading>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import Input from '@/NewLibrary/Input.vue';

const store = useStore();

const props = defineProps({
  soonestValue: {
    type: [String, Number],
    default: 24,
  },
  soonestUnit: {
    type: String,
    default: 'Hours',
  },
  furthestValue: {
    type: [String, Number],
    default: 30,
  },
  furthestUnit: {
    type: String,
    default: 'Days',
  },
  defaultAppointmentDuration: {
    type: [String, Number],
    default: 60,
  },
});

const emit = defineEmits([
  'update:soonestValue',
  'update:soonestUnit',
  'update:furthestValue',
  'update:furthestUnit',
]);

const saveLoading = ref(false);
const localSoonestValueState = ref('24');
const localSoonestUnitState = ref('Hours');
const localFurthestValueState = ref('30');
const localFurthestUnitState = ref('Days');
const originalSchedulingValues = ref({
  soonestValue: '24',
  soonestUnit: 'Hours',
  furthestValue: '30',
  furthestUnit: 'Days',
});

const localSoonestValue = computed({
  get: () => localSoonestValueState.value,
  set: value => {
    localSoonestValueState.value = value;
  },
});

const localSoonestUnit = computed({
  get: () => localSoonestUnitState.value,
  set: value => {
    localSoonestUnitState.value = value;
  },
});

const localFurthestValue = computed({
  get: () => localFurthestValueState.value,
  set: value => {
    localFurthestValueState.value = value;
  },
});

const localFurthestUnit = computed({
  get: () => localFurthestUnitState.value,
  set: value => {
    localFurthestUnitState.value = value;
  },
});

const soonestUnitOptions = [
  { label: 'Hours', value: 'Hours' },
];

const furthestUnitOptions = [
  { label: 'Days', value: 'Days' },
  { label: 'Weeks', value: 'Weeks' },
  { label: 'Months', value: 'Months' },
];

const validateNonNegativeInteger = value => /^\d*$/.test(value);

function normalizeRuleValue(value, fallback = '0') {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
}

function convertSoonestToMinutes(value, unit) {
  const normalizedValue = Number(value);
  if (!Number.isFinite(normalizedValue)) return 0;
  if (unit === 'Days') return normalizedValue * 24 * 60;
  return normalizedValue * 60;
}

function convertFurthestToDays(value, unit) {
  const normalizedValue = Number(value);
  if (!Number.isFinite(normalizedValue)) return 0;
  if (unit === 'Weeks') return normalizedValue * 7;
  if (unit === 'Months') return normalizedValue * 30;
  return normalizedValue;
}

const isSaveDisabled = computed(() => (
  saveLoading.value
  || !localSoonestValue.value
  || !localFurthestValue.value
  || (
    normalizeRuleValue(localSoonestValue.value) === originalSchedulingValues.value.soonestValue
    && localSoonestUnit.value === originalSchedulingValues.value.soonestUnit
    && normalizeRuleValue(localFurthestValue.value) === originalSchedulingValues.value.furthestValue
    && localFurthestUnit.value === originalSchedulingValues.value.furthestUnit
  )
));

watch(
  () => ({
    soonestValue: normalizeRuleValue(props.soonestValue, '24'),
    soonestUnit: 'Hours',
    furthestValue: normalizeRuleValue(props.furthestValue, '30'),
    furthestUnit: props.furthestUnit,
  }),
  value => {
    localSoonestValueState.value = value.soonestValue;
    localSoonestUnitState.value = value.soonestUnit;
    localFurthestValueState.value = value.furthestValue;
    localFurthestUnitState.value = value.furthestUnit;
    originalSchedulingValues.value = value;
  },
  { immediate: true }
);

async function handleSave() {
  if (isSaveDisabled.value) return;

  saveLoading.value = true;
  try {
    await store.dispatch('calendar/updateUserBookingRules', {
      min_notice_minutes: convertSoonestToMinutes(localSoonestValue.value, localSoonestUnit.value),
      max_advance_days: convertFurthestToDays(localFurthestValue.value, localFurthestUnit.value),
      default_appointment_duration: Number(props.defaultAppointmentDuration),
    });

    originalSchedulingValues.value = {
      soonestValue: normalizeRuleValue(localSoonestValue.value, '24'),
      soonestUnit: localSoonestUnit.value,
      furthestValue: normalizeRuleValue(localFurthestValue.value, '30'),
      furthestUnit: localFurthestUnit.value,
    };
    emit('update:soonestValue', localSoonestValue.value);
    emit('update:soonestUnit', localSoonestUnit.value);
    emit('update:furthestValue', localFurthestValue.value);
    emit('update:furthestUnit', localFurthestUnit.value);
  } finally {
    saveLoading.value = false;
  }
}
</script>

<style scoped>
.section-block {
  display: flex;
  flex-direction: column;
  /* gap: 24px; */
  padding-bottom: 24px;
  border-bottom: 1px dashed #c4c4c4;
}

.card-title {
  font-weight: 500;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #333;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 20px;
}

.field-label {
  min-width: 160px;
  color: #4A4A4A;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.scheduling-window-input :deep(.el-input__wrapper) {
  padding: 4px;
  width: 60px;
}
.scheduling-window-input :deep(.el-input__wrapper .el-input__inner) {
  text-align: center;
  padding: 0;
  height: 32px;
}

.window-select-wrap {
  margin-top: 4px;
  width: 100px;
}

.window-select :deep(.el-select__wrapper),
.scheduling-window-input :deep(.el-input__wrapper) {
  min-height: 40px;
  --el-border-color: #C9C9C9;
  color: #666;
}

.window-select :deep(.el-select__wrapper) {
  padding: 4px 8px 4px 12px;
}
</style>
